import { CalibrationReportData } from '../types/CalibrationReport';
import { CategoryDeficiencyReportData } from '../types/CategoryDeficiencyReport';
import { PerformanceTrendReportData, OperatorDrilldownResult } from '../types/PerformanceTrendReport';
import { QACoverageReportData } from '../types/QACoverageReport';
import { DigestDelivery } from '../types/DigestDelivery';
import { apiClient } from './sessionAwareApiClient';
import { ApiError } from './apiClient';
import { notificationService } from './notificationService';

/** The four reports that export server-side, matched to their route segment. */
export type ExportableReport =
  | 'calibration'
  | 'qa-coverage'
  | 'performance-trend'
  | 'category-deficiency';

export class ReportService {
  /**
   * Downloads a report as CSV.
   *
   * The export re-runs the report server-side for the period passed in, and hits the same
   * short-lived report cache the screen's own fetch populated — so for a period already on screen
   * it is the cached figures that get written, not a fresh computation that could differ. What
   * keeps the file and the page in agreement is the caller: ReportExportButton refuses to export
   * while its panel is refetching, because until that lands the period in its props is not the
   * period being displayed.
   *
   * Errors are rethrown rather than routed through notificationService: the caller renders the
   * failure inline next to the button that caused it, matching the audit trail's export.
   */
  static async exportReport(
    report: ExportableReport,
    startDate?: string,
    endDate?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const url = `/api/reports/${report}/export${params.toString() ? `?${params}` : ''}`;
    const response = await apiClient.get(url, { responseType: 'blob' });
    return response.data;
  }

  static async getCalibrationReport(
    startDate?: string,
    endDate?: string
  ): Promise<CalibrationReportData> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `/api/reports/calibration${params.toString() ? `?${params}` : ''}`;
      const response = await apiClient.get<CalibrationReportData>(url);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Calibration Report');
      throw _error;
    }
  }

  static async getPerformanceTrendReport(
    startDate?: string,
    endDate?: string
  ): Promise<PerformanceTrendReportData> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `/api/reports/performance-trend${params.toString() ? `?${params}` : ''}`;
      const response = await apiClient.get<PerformanceTrendReportData>(url);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Performance Trend Report');
      throw _error;
    }
  }

  static async getOperatorDrilldown(
    operatorId: string,
    startDate?: string,
    endDate?: string
  ): Promise<OperatorDrilldownResult> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `/api/reports/performance-trend/${encodeURIComponent(operatorId)}${params.toString() ? `?${params}` : ''}`;
      const response = await apiClient.get<OperatorDrilldownResult>(url);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Operator Drilldown');
      throw _error;
    }
  }

  /**
   * Recent scheduled-digest deliveries, newest first.
   *
   * Carries a recipient count and never the addresses — a Supervisor can read this but cannot
   * edit the distribution list, so returning it here would let them enumerate it.
   */
  static async getDigestDeliveries(): Promise<DigestDelivery[]> {
    try {
      const response = await apiClient.get<DigestDelivery[]>('/api/reports/digest-deliveries');
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Digest Delivery History');
      throw _error;
    }
  }

  static async getQACoverageReport(
    startDate?: string,
    endDate?: string
  ): Promise<QACoverageReportData> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `/api/reports/qa-coverage${params.toString() ? `?${params}` : ''}`;
      const response = await apiClient.get<QACoverageReportData>(url);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'QA Coverage Report');
      throw _error;
    }
  }

  static async getCategoryDeficiencyReport(
    startDate?: string,
    endDate?: string
  ): Promise<CategoryDeficiencyReportData> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `/api/reports/category-deficiency${params.toString() ? `?${params}` : ''}`;
      const response = await apiClient.get<CategoryDeficiencyReportData>(url);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Category Deficiency Report');
      throw _error;
    }
  }
}
